<template>
  <div class="my-profile-container">
    <div
      v-if="isMobile"
      class="back-menu mobile-position-fixed"
    >
      <mobile-back-menu :title="$t('account-settings.my-profile.title')" />
    </div>
    <el-row
      v-if="user"
      class="profile-wrapper"
      :class="user.contact_info && user.contact_info.latitude ? '' : 'custom-mb'"
    >
      <el-form
        v-if="form"
        :ref="form"
        v-loading.lock="loading"
        :model="form"
      >
        <GmapMap
          v-if="isMobile && user && user.contact_info"
          class="google-map"
          :center="{ lat: Number(user.contact_info.latitude), lng: Number(user.contact_info.longitude) }"
          :zoom="maps.zoom"
          :options="maps.options"
          map-type-id="terrain"
        >
          <GmapMarker
            :position="{ lat: Number(user.contact_info.latitude), lng: Number(user.contact_info.longitude) }"
          />
        </GmapMap>
        <div
          v-else
          class="custom-margin-bottom"
        />
        <div class="user-avatar">
          <el-upload
            class="avatar-uploader"
            :show-file-list="false"
            action
            :before-upload="beforeAvatarUpload"
          >
            <vue-load-image v-if="user && user.file">
              <img
                slot="image"
                class="avatar"
                :src="user.file.url_view | resizeImage(132, 132)"
                :data-src="user.file.url_view | resizeImage(132, 132)"
              >
              <div
                slot="preloader"
                v-loading="true"
                class="img-loading"
              >
                {{ $t('Loading ....') }}
              </div>
              <div slot="error">
                {{ $t('Network error') }}
              </div>
            </vue-load-image>
            <i
              v-else
              class="el-icon-plus avatar-uploader-icon"
            />
            <el-button
              class="edit-avatar"
              icon="icon-pencil-edit-button"
              circle
            />
          </el-upload>
          <span
            v-if="user"
            class="user-name"
          >{{ user.name }}</span>
        </div>
        <div class="flex-wrapper flex-align-center">
          <div class="personal-data">
            <h1 class="title">
              {{ $t('account-settings.my-profile.personal-data') }}
            </h1>
            <el-form-item :label="$t('account-settings.my-profile.name.label')">
              <editable
                v-model="form.name"
                :placeholder="$t('account-settings.my-profile.name.placeholder')"
                :option="option"
                :ajax-options="ajaxOptions"
                left-menu
                @change="editableChanged($event, 'name')"
              />
              <i class="icon-pencil-edit-button" />
            </el-form-item>
            <el-form-item :label="$t('account-settings.my-profile.email.label')">
              <editable
                v-model="form.email"
                :placeholder="$t('account-settings.my-profile.email.placeholder')"
                :option="option"
                :ajax-options="ajaxOptions"
                @change="editableChanged($event, 'email')"
              />
              <i class="icon-pencil-edit-button" />
            </el-form-item>
            <el-form-item
              v-if="form && form.contact_info"
              :label="$t('account-settings.my-profile.phone.label')"
            >
              <editable
                v-model="form.contact_info.phone"
                :placeholder="$t('account-settings.my-profile.phone.placeholder')"
                :option="option"
                :ajax-options="ajaxOptions"
                @change="editableChanged($event, 'contact_info.phone')"
              />
              <i class="icon-pencil-edit-button" />
            </el-form-item>
          </div>
          <div class="location-data">
            <h1 class="title">
              {{ $t('account-settings.my-profile.location.label') }}
            </h1>

            <el-form-item
              v-if="form && form.contact_info"
              class="location"
              :label="$t('account-settings.my-profile.location.label')"
              :error="error('contact_info.location') || error('contact_info.longitude') || error('contact_info.latitude')"
            >
              <div class="location-input-wrapper">
                <gmap-place-input
                  class-name="ad1-location"
                  :placeholder="$t('account-settings.my-profile.location.placeholder')"
                  :default-place="form.contact_info.location"
                  @place_changed="editableChanged($event, 'contact_info.location')"
                />
              </div>
              <i class="icon-pencil-edit-button" />
            </el-form-item>

            <GmapMap
              v-if="!isMobile && user && user.contact_info"
              class="google-map"
              :center="{ lat: Number(user.contact_info.latitude), lng: Number(user.contact_info.longitude) }"
              :zoom="maps.zoom"
              :options="maps.options"
              map-type-id="terrain"
            >
              <GmapMarker
                :position="{ lat: Number(user.contact_info.latitude), lng: Number(user.contact_info.longitude) }"
              />
            </GmapMap>
          </div>
        </div>
      </el-form>
      <el-col
        :span="8"
        class="logout-wrapper"
      >
        <el-button @click="resetPass">
          {{ $t('account-settings.my-profile.reset-password.placeholder') }}
        </el-button>
        <el-button
          class="submit-button primary"
          @click="openLogOutBox = true"
        >
          {{ $t('my-profile.account.logout-text') }}
        </el-button>
        <el-dialog
          class="custom-auth-box"
          :class="{isMobile: isMobile}"
          :title="$t('my-profile.account.logout-text')"
          :modal-append-to-body="false"
          :visible.sync="openLogOutBox"
        >
          <span>{{ $t('my-profile.account.logout.text') }}</span>
          <span
            slot="footer"
            class="dialog-footer"
          >
            <el-button
              type="primary"
              @click="openLogOutBox = false"
            >{{ $t('my-profile.account.logout.cancel') }}</el-button>
            <el-button
              type="primary"
              @click="logout"
            >{{ $t('my-profile.account.logout-text') }}</el-button>
          </span>
        </el-dialog>
      </el-col>
    </el-row>
    <el-col v-if="isMobile">
      <footer-menu />
    </el-col>
  </div>
</template>
<script>
  import { mapState } from 'vuex';
  import { gmapApi } from 'vue2-google-maps';
  import Editable from '@/components/Editable';
  import { objToFormData, dataGet } from '@fps/vue-toolkit/dist/util';
  import ResourcePage from '@/components/Core/ResourcePage';
  import MobileBackMenu from '../../components/Mobile/MobileBackMenu';
  import FooterMenu from '../../components/Mobile/FooterMenu';


  export default {
    components: {
      FooterMenu,
      MobileBackMenu,
      Editable,
    },
    extends: ResourcePage,
    data() {
      return {
        openLogOutBox: false,
        resource: 'users',
        imageUrl: '',
        form: null,
        ajaxOptions: {
          type: 'put',
          dataType: 'json',
        },
        option: {},
        maps: {
          zoom: 14,
          options: {
            fullscreenControl: false,
            mapTypeControl: false,
            panControl: false,
            rotateControl: false,
            streetViewControl: false,
            zoomControl: false,
          },
        },
      };
    },
    computed: {
      google: gmapApi,
      ...mapState({
        isMobile: state => state.isMobile,
        user: state => state.auth.user,
      }),
    },
    watch: {
      user(newVal) {
        this.updateForm(newVal);
      },
    },
    created() {
      this.updateForm(this.user);
    },
    methods: {
      resetPass() {
        this.loading = true;
        this.$store.dispatch('resetPassword', { credentials: { email: this.form.email } })
          .then(() => {
            this.loading = false;
            this.$message({ type: 'success',
              message: ` ${this.$t('message.reset-password.sent')}`,
            });
            // this.$router.push('/reset-password');
          })
          .catch(({ response }) => {
            this.onFormError(response);
          });
      },
      beforeAvatarUpload(file) {
        this.loading = true;
        this.provider.api
          .saveItem('file/upload',
            objToFormData({ file }), {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          )
          .then(({ data }) => {
            this.handleFetchUser(
              this.provider.update(this.user.id, {
                file: { id: data.id },
              }),
            );
          })
          .catch(({ response }) => {
            this.onFormError(response);
          });
        return false;
      },
      logout() {
        this.$store.dispatch('logout');
        this.openLogOutBox = false;
      },
      updateForm(data) {
        if (data) {
          this.form = {
            name: data.name || '',
            email: data.email || '',
            contact_info: {
              phone: dataGet('contact_info.phone', data),
              location: dataGet('contact_info.location', data),
            },
          };
        }
      },
      /* eslint-disable */
      editableChanged(newValue, fieldName) {
        switch (fieldName) {
          // TODO make a new XEditable without the bug of field not updating
          // if (confirm(this.$t('Are you sure you want to change your email ?'))) {
          //   this.defaultUpdateFieldLogic(fieldName, newValue);
          // } else {
          //   this.$nextTick(() => {
          //     console.log(this.form);
          //     this.updateForm(this.user);
          //   });
          // }
          // break;
          case 'email':
          case 'name':
          case 'contact_info.phone':
            this.defaultUpdateFieldLogic(fieldName, newValue);
            break;
          case 'contact_info.location':
            this.handleFetchUser(
              this.provider.update(this.user.id, {
                contact_info: {
                  location: newValue.formatted_address,
                  city: this.getCity(newValue),
                  latitude: newValue.geometry.location.lat(),
                  longitude: newValue.geometry.location.lng(),
                },
              }),
            );
            break;
          default:
        }
      },
      defaultUpdateFieldLogic(fieldName, newValue) {
        this.loading = true;
        this.handleFetchUser(
          this.provider.update(this.user.id, fieldName.split('.').reverse().reduce((o, i) => ({ [i]: o }), newValue)),
        );
      },
      getCity(newValue) {
        for (let i = 0; i < newValue.address_components.length; i++) {
          console.log(newValue.address_components[i])
          if (newValue.address_components[i]['types'].includes('locality') || newValue.address_components[i]['types'].includes('administrative_area_level_1')) {
            return newValue.address_components[i]['long_name']
          }
        }
      },
      handleFetchUser(promise) {
        promise
          .then(() => {
            this.$store.dispatch('fetchUser')
              .then(() => {
                this.loading = false;
              })
              .catch(() => {
                this.loading = false;
              });
          })
          .catch(() => {
            this.loading = false;
          });
      },
    },
  };
</script>


<style scoped>

</style>



// WEBPACK FOOTER //
// src/pages/AccountSettings/MyProfile.vue